import * as React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Dropdown, MenuProps } from "antd";
import { withTranslation, WithTranslation } from "react-i18next";
import { I18N_NS } from "Config/i18n";
import { TaskUploadFile } from "../Stores/TaskDataStore";
import { withModal, WithModal } from "Components/General/Modal/withModal";
import { BCFViewerModal } from "Components/BcfViewer/BCFViewerModal";
import { modalProps } from "Components/FilePrint/utils/modalProps";
import { documentsTableStore } from "Pages/Project/Documents/Stores/DocumentsTableStore";
import { onlineViewerStore } from "Stores/OnlineViewerStore";
import { pdfViewerStore } from "Stores/PdfViewerStore";
import { wopiViewerStore } from "Stores/WOPIViewerStore";
import { xktViewerStore } from "Components/XKTViewer/Stores/XKTViewerStore";
import { ItemType } from "antd/lib/menu/interface";
import { apryseViewerStore } from "Stores/PdfViewerStore/ApryseViewerStore";

interface TaskFilesTableItemActionsProps extends WithTranslation, WithModal {
  file: API_FileDataEntity | TaskUploadFile;
  editMode?: boolean;
  onRemove?: (v: string) => void;
}

export const TaskFilesTableItemActions = withTranslation(I18N_NS.PROJECT)(
  withModal(
    class extends React.Component<TaskFilesTableItemActionsProps> {
      handleMenuClick = ({ key, domEvent }) => {
        domEvent.preventDefault();
        domEvent.stopPropagation();
        this.doFileClick(key, this.props.file as TaskUploadFile);
      };

      get menu(): MenuProps {
        const { t, file, onRemove } = this.props;
        const menuItems: ItemType[] = [];
        if (onRemove) {
          menuItems.push({
            key: "remove",
            label: (
              <span data-testid="task-files-action-remove">
                <FontAwesomeIcon icon={["far", "trash-alt"]} fixedWidth={true} className="mr-5" />
                {t("Remove")}
              </span>
            ),
          });
        }
        if (file.fileActions) {
          if (file.fileActions.download) {
            menuItems.push({
              key: "download",
              label: (
                <span data-testid="task-files-action-download">
                  <FontAwesomeIcon icon={["far", "download"]} fixedWidth={true} className="mr-5" />
                  {t("Download")}
                </span>
              ),
            });
          }
          if (file.viewers) {
            if (file.viewers.bcfViewer) {
              menuItems.push({
                key: "bcf_view",
                label: (
                  <span data-testid="task-files-action-bcf-view">
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("Open with BCF Viewer")}
                  </span>
                ),
              });
            }
            if (file.viewers.bimViewerSupported) {
              menuItems.push({
                key: "xkt_view",
                disabled: !file.viewers.bimViewer,
                label: (
                  <span data-testid="task-files-action-xkt-view">
                    <FontAwesomeIcon icon={["far", "cube"]} fixedWidth={true} className="mr-5" />
                    {file.viewers.bimViewer ? t("View with BIM Viewer") : t("BIM Viewer (being processed)")}
                  </span>
                ),
              });
            }
            if (file.viewers.webViewer) {
              menuItems.push({
                key: "apryse_view",
                label: (
                  <span data-testid="task-files-action-web-view">
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with WebViewer")}
                  </span>
                ),
              });
            } else if (file.viewers.easyViewer) {
              menuItems.push({
                key: "easy_view",
                label: (
                  <span data-testid="task-files-action-easy-view">
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with Easy Viewer")}
                  </span>
                ),
              });
            } else if (file.viewers.office365) {
              menuItems.push({
                key: "wopi_view",
                label: (
                  <span data-testid="task-files-action-office-view">
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with Office Online")}
                  </span>
                ),
              });
            } else if (file.viewers.onlineViewer) {
              menuItems.push({
                key: "online_view",
                label: (
                  <span data-testid="task-files-action-online-view">
                    <FontAwesomeIcon icon={["far", "eye"]} fixedWidth={true} className="mr-5" />
                    {t("View with Online Viewer")}
                  </span>
                ),
              });
            }
          }
        }
        return {
          onClick: this.handleMenuClick,
          className: "documents-actions",
          items: menuItems,
        };
      }

      doFileClick = (key: string, data: TaskUploadFile | API_FileDataEntity) => {
        const { t, showModal } = this.props;

        switch (key) {
          case "download":
            documentsTableStore.downloadFiles([{ id: +data.id, name: data.name, fileActions: data.fileActions }]);
            break;
          case "bcf_view":
            showModal(BCFViewerModal, {
              modalProps: {
                ...modalProps,
                maskClosable: true,
                width: "100%",
                title: `${t("File")}: ${data.name}`,
                style: { maxWidth: "1300px" },
                className: "full-height-modal with-drawer bcf-modal",
                zIndex: 1013,
              },
              componentProps: { fileId: +data.id },
            });
            break;
          case "apryse_view":
            apryseViewerStore.fetch(+data.id, {
              fileName: data.name,
              canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
            });
            break;
          case "easy_view":
            pdfViewerStore.fetch(+data.id, {
              fileName: data.name,
              canDownload: data.fileActions.download || data.fileActions.downloadWithWatermark,
            });
            break;
          case "xkt_view":
            xktViewerStore.initViewer({
              fileName: data.name,
              fileId: data.id as number,
              parentFolderId: (data as API_FileDataEntity).parentId,
            });
            break;
          case "wopi_view":
            wopiViewerStore.fetch(+data.id, { fileName: data.name, edit: data.viewers.office365Edit });
            break;
          case "online_view":
            onlineViewerStore.fetch(+data.id, { fileName: data.name });
            break;
          case "remove":
            this.props.onRemove((data as TaskUploadFile).uid);
            break;
          default:
            break;
        }
      };
      render() {
        return (
          <Dropdown menu={this.menu} trigger={["click"]}>
            <div
              className="docs-dropdown-trigger"
              data-testid="task-files-row-actions-trigger"
              onClick={(e) => e.stopPropagation()}
            >
              <FontAwesomeIcon className="email-action-icon" icon={["far", "ellipsis-v"]} fixedWidth={true} />
            </div>
          </Dropdown>
        );
      }
    }
  )
);
